import { useEffect, useState } from "react";
import { Users, Kanban, AlertTriangle, Building2, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

export default function AgencyDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/agency/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-white/60">Carregando painel...</div>;
  if (!data) return <div className="p-8 text-red-400">Erro ao carregar dashboard.</div>;

  const stageOrder = ["novo", "em_atendimento", "qualificado", "visita", "proposta", "fechamento", "ganho", "fechado", "perdido"];
  const stageLabels = { novo: "Novo", em_atendimento: "Em atend.", qualificado: "Qualificado", visita: "Visita", proposta: "Proposta", fechamento: "Fechamento", ganho: "Ganho", fechado: "Fechado", perdido: "Perdido" };
  const stageColors = { novo: "bg-white/8 text-white", em_atendimento: "bg-cyan-500/15 text-cyan-300", qualificado: "bg-[#3B9EFF]/15 text-[#7FBEFF]", visita: "bg-emerald-500/15 text-emerald-300", proposta: "bg-amber-500/15 text-amber-300", fechamento: "bg-lime-500/15 text-lime-300", ganho: "bg-green-500/20 text-green-300", fechado: "bg-green-500/15 text-green-300", perdido: "bg-red-500/15 text-red-300" };

  return (
    <div className="p-6 lg:p-10 space-y-8" data-testid="agency-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Painel da imobiliária</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1">Visão geral</h1>
        <p className="text-white/60 text-sm mt-2">Consolidado de todos os corretores da sua imobiliária em tempo real.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <StatCard icon={Users} label="Corretores" value={data.realtor_count} color="text-[#3B9EFF]" testid="stat-realtor-count" />
        <StatCard icon={Kanban} label="Leads totais" value={data.total_leads} color="text-emerald-400" testid="stat-total-leads" />
        <Link to="/imobiliaria/agenciamentos" className="block">
          <StatCard icon={Building2} label="Agenciamentos" value={data.total_agenciamentos || 0} color="text-purple-400" testid="stat-total-agenciamentos" />
        </Link>
        <StatCard icon={AlertTriangle} label="Sem contato 7+ dias" value={data.stale_leads_count} color="text-amber-400" testid="stat-stale-leads" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Distribuição por etapa</h2>
          <div className="space-y-2">
            {stageOrder.map((s) => {
              const count = data.by_stage[s] || 0;
              const pct = data.total_leads ? Math.round((count / data.total_leads) * 100) : 0;
              return (
                <div key={s} className="flex items-center gap-3">
                  <div className={`w-24 text-xs font-semibold px-2.5 py-1 rounded ${stageColors[s]} text-center`}>{stageLabels[s]}</div>
                  <div className="flex-1 h-6 bg-white/5 rounded-md overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#3B9EFF] to-[#7FBEFF]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-16 text-right text-sm text-white/80 font-mono">{count}</div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="font-heading font-bold text-lg">Top corretores</h2>
            <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">por leads</span>
          </div>
          {data.top_realtors.length === 0 ? (
            <div className="text-white/50 text-sm">Nenhum lead cadastrado ainda.</div>
          ) : (
            <div className="space-y-2">
              {data.top_realtors.map((r, i) => (
                <Link key={r.realtor_id} to={`/imobiliaria/leads?realtor_id=${r.realtor_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/4 hover:bg-white/8 transition"
                  data-testid={`top-realtor-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center text-sm font-bold">{r.realtor_name?.[0]?.toUpperCase() || "?"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{r.realtor_name}</div>
                    <div className="text-xs text-white/50">{r.lead_count} lead{r.lead_count !== 1 ? "s" : ""}</div>
                  </div>
                  <div className="text-xs font-mono text-[#3B9EFF]">#{i + 1}</div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Ranking de Captação */}
      <section className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-6" data-testid="ranking-captacao">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h2 className="font-heading font-bold text-lg">Ranking de captação</h2>
            <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">quem mais agencia imóveis</span>
          </div>
          <Link to="/imobiliaria/agenciamentos" className="text-xs text-[#3B9EFF] hover:underline">
            Ver todos os agenciamentos →
          </Link>
        </div>
        {(!data.top_captadores || data.top_captadores.length === 0) ? (
          <div className="text-white/50 text-sm">Nenhum agenciamento captado ainda. Peça pros corretores mandarem foto de placa no WhatsApp pra cadastrar.</div>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {data.top_captadores.map((c, i) => {
              const podium = i === 0 ? "text-amber-400" : i === 1 ? "text-stone-300" : i === 2 ? "text-orange-400" : "text-white/50";
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
              return (
                <Link key={c.realtor_id} to={`/imobiliaria/agenciamentos?realtor_id=${c.realtor_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/4 hover:bg-white/8 transition border border-white/6"
                  data-testid={`top-captador-${i}`}>
                  <div className={`text-2xl font-mono ${podium} w-10 text-center flex-shrink-0`}>{medal}</div>
                  <div className="w-9 h-9 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {c.realtor_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{c.realtor_name}</div>
                    <div className="text-xs text-white/50 flex items-center gap-2">
                      <span><b className="text-white/80">{c.total_captured}</b> captado{c.total_captured !== 1 ? "s" : ""}</span>
                      {c.agenciados > 0 && (
                        <span className="text-emerald-400">· {c.agenciados} fechado{c.agenciados !== 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className={`font-heading text-3xl font-extrabold ${color}`}>{value}</div>
      <div className="text-xs text-white/50 uppercase tracking-wide mt-1 font-semibold">{label}</div>
    </div>
  );
}
